import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert';

const ManagerList = () => {
  const navigate = useNavigate();
  const [managers, setManagers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });

  useEffect(() => {
    loadManagers(currentPage);
  }, [currentPage]);

  const loadManagers = async (page = 1) => {
    try {
      setLoading(true);
      const response = await fetch(`/api/managers?page=${page}`, {
        credentials: 'same-origin'
      });
      if (response.ok) {
        const data = await response.json();
        setManagers(data.managers || data);
        setPagination(data.pagination);
      } else {
        setAlert({ show: true, message: 'Failed to load managers', type: 'error' });
      }
    } catch (err) {
      console.error('Failed to load managers:', err);
      setAlert({ show: true, message: 'Failed to load managers', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id, fullName) => {
    if (!confirm(`Are you sure you want to delete ${fullName}? This will remove the manager but keep their associated surveys.`)) {
      return;
    }

    try {
      const response = await fetch(`/api/managers/${id}`, {
        method: 'DELETE',
        credentials: 'same-origin'
      });

      if (response.ok) {
        setAlert({ show: true, message: 'Manager deleted successfully', type: 'success' });
        loadManagers(currentPage);
      } else {
        setAlert({ show: true, message: 'Failed to delete manager', type: 'error' });
      }
    } catch (err) {
      console.error('Error deleting manager:', err);
      setAlert({ show: true, message: 'Failed to delete manager', type: 'error' });
    }
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const styles = {
    container: {
      padding: '2rem',
      backgroundColor: 'white',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    title: {
      fontSize: '2rem',
      fontWeight: 'bold',
      color: '#2c3e50',
      margin: 0
    },
    createBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#07638e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '1rem',
      fontWeight: '500'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      backgroundColor: '#f8f9fa',
      padding: '1rem',
      textAlign: 'left',
      fontWeight: 'bold',
      borderBottom: '2px solid #dee2e6'
    },
    td: {
      padding: '1rem',
      borderBottom: '1px solid #dee2e6'
    },
    actionBtn: {
      padding: '0.5rem 1rem',
      marginRight: '0.5rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    editBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    deleteBtn: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    badge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: '600',
      display: 'inline-block'
    },
    activeBadge: {
      backgroundColor: '#d4edda',
      color: '#155724'
    },
    inactiveBadge: {
      backgroundColor: '#f8d7da',
      color: '#721c24'
    },
    loading: {
      textAlign: 'center',
      padding: '3rem',
      color: '#6c757d'
    },
    noData: {
      textAlign: 'center',
      padding: '3rem',
      color: '#6c757d'
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem',
      padding: '1rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    pageButtonActive: {
      backgroundColor: '#07638e', 
      color: 'white',
      borderColor: '#07638e'
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    pageInfo: {
      fontSize: '0.875rem',
      color: '#6c757d'
    },
    pageActionButton: {
      padding: "6px 12px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      background: "#F7F7F7",
      cursor: "pointer",
    },
    pageActionDropDown: {
      position: "absolute",
      top: "40px",
      right: 0,
      background: "#fff",
      border: "1px solid #ddd",
      borderRadius: "8px",
      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
      width: "160px",
      zIndex: 10,
    },
    pageActionEditButton: {
      padding: "8px 12px",
      width: "100%",
      textAlign: "left",
      background: "none",
      border: "none",
      cursor: "pointer",
      fontSize: "0.95rem",
      borderBottom: "1px solid #eee",
    },
    pageActionDeleteButton: {
      padding: "8px 12px",
      width: "100%",
      textAlign: "left",
      background: "none",
      border: "none",
      cursor: "pointer",
      color: "red",
      fontSize: "0.95rem",
      borderBottom: "1px solid #eee",
    },
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading managers...</div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <Alert 
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <div style={styles.header}>
        <h1 style={styles.title}>Managers</h1>
        <button 
          style={styles.createBtn}
          onClick={() => navigate('/admin/managers/new')}
        >
          Create New Manager
        </button>
      </div>

      {managers.length === 0 ? (
        <div style={styles.noData}>
          No managers found. Create your first manager to get started!
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Name</th>
              <th style={styles.th}>Email</th>
              <th style={styles.th}>Department</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {managers.map(manager => (
              <tr key={manager.id}>
                <td style={styles.td}>{manager.full_name}</td>
                <td style={styles.td}>{manager.email}</td>
                <td style={styles.td}>{manager.department}</td>
                <td style={styles.td}>
                  <span 
                    style={{
                      ...styles.badge,
                      ...(manager.status === 'active' ? styles.activeBadge : styles.inactiveBadge)
                    }}
                  >
                    {manager.status === 'active' ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={styles.td}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === manager.id ? null : manager.id)}
                      style={styles.pageActionButton}
                    >
                      ⋮
                    </button>

                    {openDropdown === manager.id && (
                      <div
                        style={styles.pageActionDropDown}
                      >
                        <button
                          onClick={() => navigate(`/admin/managers/${manager.id}/edit`)}
                          style={styles.pageActionEditButton}
                        >
                          Edit
                        </button>

                        <button
                          onClick={() => handleDelete(manager.id, manager.full_name)}
                          style={styles.pageActionDeleteButton}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pagination && pagination.total_pages > 1 && (
        <div style={styles.paginationContainer}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              ...styles.pageButton,
              ...(currentPage === 1 ? styles.pageButtonDisabled : {})
            }}
          >
            ← Previous
          </button>
          
          <div style={{display: 'flex', gap: '0.5rem'}}>
            {[...Array(pagination.total_pages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === pagination.total_pages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      ...styles.pageButton,
                      ...(page === currentPage ? styles.pageButtonActive : {})
                    }}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 2 ||
                page === currentPage + 2
              ) {
                return <span key={page} style={{padding: '0.5rem'}}>...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.total_pages}
            style={{
              ...styles.pageButton,
              ...(currentPage === pagination.total_pages ? styles.pageButtonDisabled : {})
            }}
          >
            Next →
          </button>

          <span style={styles.pageInfo}>
            Page {currentPage} of {pagination.total_pages} ({pagination.total_count} total)
          </span>
        </div>
      )}
    </div>
  );
};

export default ManagerList;
